/**
 * A postal address as every surface prints it (F9).
 *
 * The wire carries an address in FIELDS - the street, an optional second line,
 * the postal code and the city - and five surfaces each joined them their own
 * way: the counter tablet, the guest's checkout banner, the guest's support
 * mail, Internal's pairing sheet and the partner's tables. They agreed by
 * accident, and the guest's line printed ", , " for a second line stored as an
 * empty string. This is the one join.
 *
 * The order is the French envelope's: the street, then the second line, then
 * the postal code and the city on one line. The country is deliberately absent,
 * as it is everywhere in this repository that renders an address: every
 * property the platform serves is in Paris.
 */

export interface AddressParts {
  readonly addressLine1: string;
  readonly addressLine2?: string | null;
  readonly postcode: string;
  readonly city: string;
}

/**
 * The address's non-empty lines: street, second line, then "75003 Paris".
 * The city line is always last whenever it has anything in it, so a surface
 * that sets it apart (a muted line in a table) can take the last entry.
 */
export function addressLines(address: AddressParts): readonly string[] {
  // Read defensively: a row a seed or a fixture wrote without a part prints
  // the parts it has rather than throwing over the one it lacks.
  const part = (value: string | null | undefined): string => (value ?? '').trim();
  const cityLine = [part(address.postcode), part(address.city)]
    .filter((value) => value !== '')
    .join(' ');
  return [part(address.addressLine1), part(address.addressLine2), cityLine].filter(
    (line) => line !== '',
  );
}

/** The same lines on one: "12 rue de Bretagne, Apt 4B, 75003 Paris". */
export function addressLine(address: AddressParts): string {
  return addressLines(address).join(', ');
}
